import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {UserService} from './user.service';
import {tap} from 'rxjs/operators';
import {IsAuthorizedService} from './is-authorized.service';
import {LoginInfo} from '../../../../projects/api/src/authentication/login-info.model';
import {UsernotificationModel} from '../../../../projects/api/src/usernotification/usernotification.model';
import {NotifymethodModel} from '../../../../projects/api/src/notifymethod/notifymethod.model';
import {TotpSecretModel} from '../../../../projects/api/src/authentication/totp-secret.model';
import {
  isTwoFactorLoginModel,
  TwoFactorLoginModel,
  TwoFactorType
} from '../../../../projects/api/src/authentication/twofactor.model';
import {
  DEFAULT_SCREEN_CHART,
  DEFAULT_SCREEN_EVENTSLOG,
  DEFAULT_SCREEN_HOME,
  DEFAULT_SCREEN_MAP
} from '../../login/login-form/default-screen.const';
import {Router} from '@angular/router';
import {ConfigureService} from '../../../../projects/common/modules/configure/configure.service';
import {TranslateService} from '@ngx-translate/core';
import {InsparkMessageService} from '@inspark/inspark-components';
import {LocalStorageService} from '../../../../projects/api/src/locale-storage/locale-storage.service';

@Injectable()
export class AuthService {
  isLoggedIn = false;
  // store the URL so we can redirect after logging in
  redirectUrl: string;
  public isAuthenticated = new Observable<boolean>(observer => {
    setTimeout(() => {
      const storageUser: LoginInfo = this.localStorageApi.load(false, 'user');

      if (this.isAuthorizedService.isAuthorized.getValue()) {
        observer.next(true);
      } else if (storageUser && storageUser.authenticated) {
        this.userService.initialize(storageUser);
        observer.next(true);
      } else {
        this._userstatus().subscribe(data => {
          if (isTwoFactorLoginModel(data)) {
            this.router.navigate(['/', 'login', 'twofactorform'], {queryParams: data});
            return;
          }
          if (!data.authenticated) {
            observer.next(false);
            return;
          }
          this.userService.initialize(data);

          observer.next(true);

        }, error => observer.next(false));

      }
    }, 100);
  });

  private APIUrl = environment.backEndUrlPrefix;
  private authPrefix = '/auth';
  private twofactorauth = '/auth/twofactorauth';
  private favoritesPrefix = '/db/user/profile/favorites';


  constructor(private _http: HttpClient,
              private userService: UserService,
              private isAuthorizedService: IsAuthorizedService,
              private localStorageApi: LocalStorageService,
              private router: Router,
              private config: ConfigureService,
              private translate: TranslateService,
              private messageService: InsparkMessageService,) {
  }

  public loginByCredentials(username, password, options) {
    // let rememberMe = ;
    // const that = this;

    return this._login(username, password, options.rememberMe, options.browserType, options.browserVersion, options.os, options.deviceInfo, options.screenSize)
      .pipe(
        tap(async (response) => {
          if (isTwoFactorLoginModel(!response)) {
            this.userService.initialize(response);
          }
        })
      );
  }

  public loginByToken(token: string) {
    return this._loginByToken(token)
      .pipe(
        tap(response => {
          this.userService.initialize(response);
        })
      );
  }

  public loginBy2FA(type: TwoFactorType, code: string) {
    return this._http.post<LoginInfo>(`${this.APIUrl}${this.authPrefix}/twofactorauth/${type}/verify`, {}, {params: {code}});
  }

  public logoutUserLocal() {
    if (!this.isAuthorizedService.isAuthorized.getValue()) {
      return false;
    }

    this.userService.logout();
    let currentUrl = '';
    try {
      currentUrl = window.location.href.split('#!')[1];
    } catch (e) {
      console.log(e.message);
    }
    window.location.href = '#!/login?backurl=' + currentUrl;
    window.location.reload();
    return true;
  }

  public logout() {
    this._logout().subscribe(success => this.logoutUserLocal(), error => this.logoutUserLocal());
  }

  public twofactorTypes(): Observable<{
    totpEnabled: boolean;
    emailContact: null | NotifymethodModel;
    smsContact: null | NotifymethodModel;
  }> {
    return this._http.get<{
      totpEnabled: boolean;
      emailContact: null | NotifymethodModel;
      smsContact: null | NotifymethodModel;
    }>(this.APIUrl + this.twofactorauth + '/types');
  }

  public twofactorEnable(type: string, contact: UsernotificationModel | boolean, passwordOrCode: {
    password?: string;
    emailCode?: string;
    smsCode?: string;
    totpCode?: string;
  }) {

    return this._http.post<TotpSecretModel>(`${this.APIUrl}${this.twofactorauth}/${type}/on`,
      contact === true ? {} : contact,
      {params: passwordOrCode});
  }

  public twofactorSendCode(type: string) {
    return this._http.post<{
      message: string;
    }>(`${this.APIUrl}${this.twofactorauth}/${type}/send_code`, {}, {params: {type}});
  }

  public twofactorTOTPConfirm(code: string) {
    return this._http.post<{
      message: string;
    }>(`${this.APIUrl}${this.twofactorauth}/totp/confirm`, {}, {params: {code}});
  }

  public twofactorTOTPSecret() {
    return this._http.get<{
      message: string;
    }>(`${this.APIUrl}${this.twofactorauth}/totp/secret`);
  }

  public twofactorDisable(type: string, contact: UsernotificationModel | boolean, passwordOrCode: {
    password?: string;
    emailCode?: string;
    smsCode?: string;
    totpCode?: string;
  }) {
    return this._http.post(`${this.APIUrl}${this.twofactorauth}/${type}/off`,
      contact === true ? {} : contact,
      {params: passwordOrCode});
  }

  async completeLogin(user: LoginInfo, backUrl?: string) {
    if (user && user.authenticated && window.location.href.indexOf('/#!/login?client_id=') !== -1) {
      const inx = window.location.href.indexOf('/#!/login?client_id=');
      const client_id = window.location.href.substr(inx + '/#!/login?client_id='.length);

      window.open(`${environment.backEndUrlPrefix}/oauth2/authorize?client_id=${encodeURIComponent(client_id)}`, '_self');
    }

    const defaultTransitionPromise = (userData) => {
      if (!userData.privileges || userData.privileges.length === 0) {
        this.translate.get('No access to the system. Contact the administrator!').subscribe((res: string) => {
          this.messageService.error(res + (+this.translate.currentLang ? '' : ' '));
        });
        return this.router.navigateByUrl('/main/user/profile');
      }

      if (backUrl) {
        return this.router.navigateByUrl(backUrl);
      }

      if (this.config.options.appConfig.typeOfDefaultUrl === 'static') {
        this.router.navigateByUrl(this.config.options.appConfig.defaultUrl);
      }
      // -------------
      // временное решение по определению приложения в форме login.
      // Для приложения /app/ загружается предустановленное приложение в профиде пользователя, для корневого приложения только в /main.
      // Этот кусок кода переделать после портации админки в папку /admin/ и решения запроса SEM-6072
      //---------------------------------------------------------------------
      if (this.config.options.appConfig.typeOfDefaultUrl === 'dynamic' && window.location.href.indexOf('/app/#!/login') !== -1) {
        switch (userData.defaultScreen) {
          case DEFAULT_SCREEN_EVENTSLOG:
            return this.router.navigateByUrl('/main/eventslog');
          case DEFAULT_SCREEN_CHART:
            return this.router.navigateByUrl('/main/charts');
          case DEFAULT_SCREEN_MAP:
            return this.router.navigateByUrl('/main/map');
          case DEFAULT_SCREEN_HOME:
            return this.router.navigateByUrl('/main');
          default:
            return this.router.navigateByUrl(`/main/dashboard/${userData.defaultScreen}`);

        }
      } else {
        return this.router.navigateByUrl('/app/home');
      }
    };
    return defaultTransitionPromise(user);
  }

  private _login(username: string, password: string, rememberme?: boolean,
                 browserType?: string, browserVersion?: string, os?: string, deviceInfo?: string, screenSize?: string) {
    return this._http.post<LoginInfo | TwoFactorLoginModel>(`${this.APIUrl}${this.authPrefix}/login`,
      `username=${username}&password=${password}${rememberme ? `&rememberme=${rememberme}` : ''}` +
      `${browserType ? `&browserType=${browserType}` : ''}` +
      `${browserVersion ? `&browserVersion=${browserVersion}` : ''}` +
      `${os ? `&os=${os}` : ''}` +
      `${deviceInfo ? `&deviceInfo=${deviceInfo}` : ''}` +
      `${screenSize ? `&screenSize=${screenSize}` : ''}`,
      {
        headers: new HttpHeaders()
          .set('Content-Type', 'application/x-www-form-urlencoded')
      });

  }

  private _loginByToken(token: string) {
    return this._http.post<LoginInfo>(`${this.APIUrl}${this.authPrefix}/sso`,
      `token=${encodeURIComponent(token)}`,
      {
        headers: new HttpHeaders()
          .set('Content-Type', 'application/x-www-form-urlencoded')
      });

  }

  private _logout() {
    return this._http.post(`${this.APIUrl}${this.authPrefix}/logout`, null);
  }

  private _recovery_password(username: string) {
    return this._http.post(`${this.APIUrl}${this.authPrefix}/recovery_password`, null, {params: {username}});
  }

  private _reset_password(username: string, code: string, password: string) {
    return this._http.post(`${this.APIUrl}${this.authPrefix}/reset_password`, null, {
      params: {
        username,
        code,
        password
      }
    });
  }

  private _userstatus() {
    return this._http.get<LoginInfo>(`${this.APIUrl}${this.authPrefix}/userstatus`);
  }
}
